/**
 * Router Guards - Authentication and authorization middleware
 * Handles route protection and user permissions
 */

import { useUserStore } from "../stores/user";
import { useOrganizationStore } from "../stores/organization";
import { CURRENT_POLICY_VERSION } from "../services/firebase";

export const setupRouterGuards = (router) => {
  router.beforeEach(async (to, from, next) => {
    const userStore = useUserStore();
    const organizationStore = useOrganizationStore();

    // Wait for auth state to be determined
    await userStore.waitForAuthState();

    // Check if route requires authentication
    if (to.meta.requiresAuth) {
      if (!userStore.isAuthenticated) {
        next("/login");
        return;
      }

      // Check if user has organization (except for select-org and onboarding pages)
      if (!userStore.hasOrganization && 
          !['/select-org', '/onboarding'].includes(to.path)) {
        // Wait for claims to be refreshed (Cloud Function might still be setting up)
        try {
          await userStore.refreshClaims();
          // If still no organization after refresh, redirect to select-org
          if (!userStore.hasOrganization) {
            next("/select-org");
            return;
          }
        } catch (error) {
          console.error("Error refreshing claims in router guard:", error);
          // If there's an error, still redirect to select-org
          next("/select-org");
          return;
        }
      }

      // Check custom claims policy version
      if (userStore.hasOrganization && 
          userStore.policyVersion !== CURRENT_POLICY_VERSION) {
        try {
          await userStore.refreshClaims();
        } catch (error) {
          console.error("Error refreshing claims:", error);
        }
      }

      // Load current organization if not loaded
      if (userStore.hasOrganization && !organizationStore.currentOrganization) {
        try {
          await organizationStore.loadCurrentOrganization(userStore.currentOrgId);
        } catch (error) {
          console.error("Error loading organization:", error);
        }
      }

      // Check role-based permissions
      if (to.meta.requiredRole) {
        const requiredRoles = Array.isArray(to.meta.requiredRole) 
          ? to.meta.requiredRole 
          : [to.meta.requiredRole];
        
        if (!userStore.hasRole(requiredRoles)) {
          next("/");
          return;
        }
      }

      // Check permission-based access
      if (to.meta.permissions) {
        const hasPermission = to.meta.permissions.some((permission) =>
          userStore.canAccess(permission)
        );

        if (!hasPermission) {
          next("/");
          return;
        }
      }
    }

    // Redirect authenticated users with org away from login/select-org
    if (userStore.isAuthenticated && userStore.hasOrganization) {
      if (['/login', '/select-org', '/onboarding'].includes(to.path)) {
        next("/");
        return;
      }
    }

    next();
  });

  router.afterEach((to) => {
    const title = to.meta.title || "Zerv Admin Dashboard";
    document.title = `${title} - Zerv`;
  });
};
